import { useState } from "react";
import { BackButton } from "../../../components/buttons/backButton";
import { NumberCard } from "../../../components/cards/numberCard";
import { TabNav } from "../../../components/tabNav";
import { PageHeader } from "../../../components/typography/pageHeader";
import { BgWrapper } from "../../../components/wrapper/bgWrapper";
import {
  useGetMonthlyEventGuestListQuery,
  useGetMonthlyEventSummaryQuery,
} from "../../../redux/features/admin/event/events.api";
import { getMonthAbbreviation } from "../../../utils/helpers";
import { QueryParams } from "../../../utils/types";
import { useAdminFinancialSummaryQuery } from "../../../redux/features/admin/payments/payments.api";
import { useParams } from "react-router-dom";
import { skipToken } from "@reduxjs/toolkit/query";
import { MonthlyEvents } from "./monthlyEvents";
import { MonthlyGuests } from "./monthlyGuests";

type Tabs = "events" | "guests";

export const MonthlyRevenuePage = () => {
  const [activeTab, setActiveTab] = useState<Tabs>("events");
  const { monthId, yearId } = useParams();

  const [query, setQuery] = useState<QueryParams>({
    page: 1,
    limit: 50,
    search: undefined,
  });

  const [monthlyQuery, setMonthlyQuery] = useState<QueryParams>({
    page: 1,
    limit: 50,
    search: undefined,
  });

  const tabs = [
    {
      title: "Events",
      to: "events",
      onClick: (selected: string) => {
        setActiveTab(selected as Tabs);
        setMonthlyQuery({
          ...monthlyQuery,
          search: undefined,
        });
      },
    },
    {
      title: "Guests",
      to: "guests",
      onClick: (selected: string) => {
        setActiveTab(selected as Tabs);
      },
    },
  ];

  const { isFetching: fetchingEarnings, data: financialEarnings } =
    useAdminFinancialSummaryQuery(
      monthId && yearId
        ? {
            month: monthId,
            year: yearId,
          }
        : skipToken,
      {
        refetchOnMountOrArgChange: true,
      },
    );

  const { isFetching: fetchingEvents, data: eventsData } =
    useGetMonthlyEventSummaryQuery(
      monthId && yearId
        ? {
            month: monthId,
            year: yearId,
            ...monthlyQuery,
          }
        : skipToken,
      {
        refetchOnMountOrArgChange: true,
      },
    );

  const { isFetching: fetchingEventGuest, data: guestData } =
    useGetMonthlyEventGuestListQuery(
      monthId && yearId
        ? {
            month: monthId,
            year: yearId,
            ...query,
          }
        : skipToken,
      {
        refetchOnMountOrArgChange: true,
      },
    );

  return (
    <div className="w-full">
      <BackButton />
      <PageHeader
        header={`Revenue (${getMonthAbbreviation(monthId || "")} ${yearId})`}
        className="mt-2 font-unbounded font-normal"
      />
      <BgWrapper className="mt-8 grid w-full grid-cols-1 gap-[10px] !rounded-b-none md:grid-cols-3">
        <NumberCard
          value={financialEarnings?.data?.totalPaidEvents ?? 0}
          text="Paid Events"
          isCurrency={false}
          loading={fetchingEarnings}
          titleFirst
        />
        <NumberCard
          value={financialEarnings?.data?.totalPaidTickets ?? 0}
          text="Paid Tickets Sold"
          isCurrency={false}
          loading={fetchingEarnings}
          titleFirst
        />
        <NumberCard
          value={financialEarnings?.data?.totalPaidTicketAmount ?? 0}
          text="Paid Ticket Amount"
          isCurrency
          loading={fetchingEarnings}
          titleFirst
          decimalPlaces={2}
        />
        <NumberCard
          value={financialEarnings?.data?.totalAmountPaid ?? 0}
          text="Amount Paid"
          isCurrency
          loading={fetchingEarnings}
          titleFirst
          decimalPlaces={2}
        />
        <NumberCard
          value={financialEarnings?.data?.revenue ?? 0}
          text="Revenue"
          isCurrency
          loading={fetchingEarnings}
          titleFirst
          decimalPlaces={2}
        />
        <NumberCard
          value={financialEarnings?.data?.profit ?? 0}
          text="Profiit"
          isCurrency
          loading={fetchingEarnings}
          titleFirst
          decimalPlaces={2}
        />
      </BgWrapper>
      <BgWrapper className="mt-[2px] w-full !rounded-none">
        <TabNav
          navs={tabs}
          className="!min-w-full md:!min-w-fit md:!max-w-fit"
          activeTab={activeTab}
          variant="switcher"
          theme="plain"
        />
      </BgWrapper>
      {activeTab === "events" && (
        <MonthlyEvents
          eventsData={eventsData!}
          monthlyQuery={monthlyQuery}
          setMonthlyQuery={setMonthlyQuery}
          fetchingEvents={fetchingEvents}
        />
      )}

      {activeTab === "guests" && (
        <MonthlyGuests
          fetchingEventGuest={fetchingEventGuest}
          query={query}
          setQuery={setQuery}
          eventsData={eventsData!}
          guestData={guestData!}
          monthlyQuery={monthlyQuery}
          setMonthlyQuery={setMonthlyQuery}
        />
      )}
    </div>
  );
};
